import React from 'react';
import {
  Show,
  SimpleShowLayout,
  useRecordContext,
  useRedirect,
  useTranslate
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton
} from '@mui/material';
import {
  Receipt,
  Person,
  Payment,
  ArrowBack,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Cancel,
  CreditCard,
  MonetizationOn,
  Bolt
} from '@mui/icons-material';
import { format } from 'date-fns';

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'paid': return 'success';
    case 'pending': return 'warning';
    case 'failed': return 'error';
    case 'refunded': return 'info';
    default: return 'default';
  }
};

// Page header component
const PageHeader = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const translate = useTranslate();

  if (!record) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle />;
      case 'pending': return <Schedule />;
      case 'failed': return <ErrorIcon />;
      case 'refunded': return <Cancel />;
      default: return <Receipt />;
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: { xs: 2, sm: 4 }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={() => redirect('/rfid-transactions')}
          size="large"
          sx={{
            flexShrink: 0,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#F5F5F5',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
            {translate('resources.rfid-transactions.show.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Chip
              icon={getStatusIcon(record.payment_status)}
              label={translate(`resources.rfid-transactions.paymentStatuses.${record.payment_status}`, { _: record.payment_status })}
              color={getStatusColor(record.payment_status) as any}
              size="small"
            />
            <Typography variant="body2" color="textSecondary">
              #{record.id}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Transaction Info Card
const TransactionInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Receipt sx={{ color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.rfid-transactions.show.transactionInfo')}</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.transactionId')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.id}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.labels.paymentStatus')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  <Chip
                    label={translate(`resources.rfid-transactions.paymentStatuses.${record.payment_status}`, { _: record.payment_status })}
                    color={getStatusColor(record.payment_status) as any}
                    size="small"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.labels.amount')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  <Typography variant="h6" fontWeight="600">
                    HKD {record.amount?.toFixed(2) || '0.00'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.labels.paymentMethod')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.payment_method ? translate(`resources.rfid-transactions.payment_methods.${record.payment_method}`, { _: record.payment_method }) : '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.invoiceNumber')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.invoice_number || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.invoiceDate')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.invoice_date ? format(new Date(record.invoice_date), 'PPp') : '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.dueDate')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.due_date ? format(new Date(record.due_date), 'PP') : '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.paidDate')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.paid_date ? format(new Date(record.paid_date), 'PPp') : '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.createdAt')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.created_at ? format(new Date(record.created_at), 'PPpp') : '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

// RFID User Info Card
const RfidUserInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.rfid_user) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'info.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Person sx={{ color: 'info.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.rfid-transactions.show.rfidUserInfo')}</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.userId')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.rfid_user_id}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.name')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.rfid_user?.name || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.email')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.rfid_user?.email || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.phone')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.rfid_user?.phone || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

// Charging Session Details Card
const ChargingSessionDetails = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.session_details) return null;

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Bolt sx={{ color: 'success.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.rfid-transactions.show.chargingSession')}</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.sessionId')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}><code>{record.session_details.app_session_id}</code></TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.charger')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.session_details.charger_name} ({record.session_details.charger_id})</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.site')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.session_details.site_name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.startTime')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{format(new Date(record.session_details.start_timestamp), 'PPp')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.endTime')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{format(new Date(record.session_details.charging_completed_at), 'PPp')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.duration')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{formatDuration(record.session_details.duration_seconds)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.energyDelivered')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.session_details.energy_delivered?.toFixed(3)} kWh</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.finalCost')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  <Typography variant="h6" fontWeight="600" color="success.main">
                    HKD {record.session_details.final_cost?.toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

// Cost Breakdown Card
const CostBreakdown = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.cost_breakdown) return null;

  const energyCosts = record.cost_breakdown.energy_costs || [];

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <MonetizationOn sx={{ color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.rfid-transactions.show.costBreakdown')}</Typography>
        </Box>

        {energyCosts.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {translate('resources.rfid-transactions.show.energyCostsByWindow')}
            </Typography>
            {energyCosts.map((cost: any, index: number) => (
              <Paper key={index} elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Chip label={cost.window || 'Standard'} size="small" color="primary" />
                  <Typography variant="h6" fontWeight="600">
                    HKD {cost.cost?.toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {cost.energy_kwh?.toFixed(3)} kWh @ HKD {cost.rate}/kWh
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {cost.from_timestamp_local} - {cost.to_timestamp_local}
                </Typography>
              </Paper>
            ))}
          </>
        )}

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: { xs: '55%', sm: '60%' } }}><strong>{translate('resources.rfid-transactions.show.totalEnergyCost')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }} align="right">HKD {record.cost_breakdown.total_energy_cost?.toFixed(2) || '0.00'}</TableCell>
              </TableRow>
              {record.cost_breakdown.total_idle_cost != null && record.cost_breakdown.total_idle_cost > 0 && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '55%', sm: '60%' } }}><strong>{translate('resources.rfid-transactions.show.totalIdleCost')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }} align="right">HKD {record.cost_breakdown.total_idle_cost.toFixed(2)}</TableCell>
                </TableRow>
              )}
              {record.session_fee != null && record.session_fee > 0 && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '55%', sm: '60%' } }}><strong>{translate('resources.rfid-transactions.show.sessionFee')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }} align="right">HKD {record.session_fee.toFixed(2)}</TableCell>
                </TableRow>
              )}
              {record.time_fee != null && record.time_fee > 0 && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '55%', sm: '60%' } }}><strong>{translate('resources.rfid-transactions.show.timeFee')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }} align="right">HKD {record.time_fee.toFixed(2)}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell sx={{ width: { xs: '55%', sm: '60%' } }}><strong>{translate('resources.rfid-transactions.show.finalTotal')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }} align="right">
                  <Typography variant="h6" fontWeight="600" color="primary.main">
                    HKD {record.cost_breakdown.final_total_cost?.toFixed(2) || '0.00'}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="textSecondary">
            {translate('resources.rfid-transactions.show.timezone')}: {record.cost_breakdown.timezone_used}<br />
            {translate('resources.rfid-transactions.show.calculation')}: {record.cost_breakdown.calculation_method ? translate(`resources.rfid-transactions.calculationMethods.${record.cost_breakdown.calculation_method}`, { _: record.cost_breakdown.calculation_method }) : '-'}<br />
            {translate('resources.rfid-transactions.show.calculatedAt')}: {record.cost_breakdown.calculation_timestamp ?
              format(new Date(record.cost_breakdown.calculation_timestamp), 'PPpp') : '-'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Billing Period Card
const BillingInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.billing_period) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'warning.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <MonetizationOn sx={{ color: 'warning.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.rfid-transactions.show.billingInfo')}</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.billingPeriod')}</strong></TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>{record.billing_period}</TableCell>
              </TableRow>
              {record.vendor && (
                <>
                  <TableRow>
                    <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.vendor')}</strong></TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{record.vendor.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.vendorEmail')}</strong></TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{record.vendor.email}</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

// Notes Card
const Notes = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.notes) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.rfid-transactions.show.notes')}</Typography>
        </Box>
        <Typography variant="body1">{record.notes}</Typography>
      </CardContent>
    </Card>
  );
};

// Payment Details Card
const PaymentDetailsCard = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record || !record.payment_details) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            borderRadius: 2,
            backgroundColor: 'warning.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}>
            <Payment sx={{ color: 'warning.main' }} />
          </Box>
          <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{translate('resources.rfid-transactions.show.paymentDetails')}</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableBody>
              {record.payment_details.site_id && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.siteId')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}><code>{record.payment_details.site_id}</code></TableCell>
                </TableRow>
              )}
              {record.payment_details.charger_id && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.chargerId')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}><code>{record.payment_details.charger_id}</code></TableCell>
                </TableRow>
              )}
              {record.payment_details.vendor_id && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.vendorId')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}><code>{record.payment_details.vendor_id}</code></TableCell>
                </TableRow>
              )}
              {record.payment_details.start_timestamp && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.sessionStart')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{format(new Date(record.payment_details.start_timestamp), 'PPp')}</TableCell>
                </TableRow>
              )}
              {record.payment_details.charging_completed_at && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.sessionEnd')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{format(new Date(record.payment_details.charging_completed_at), 'PPp')}</TableCell>
                </TableRow>
              )}
              {record.payment_details.created_at && (
                <TableRow>
                  <TableCell sx={{ width: { xs: '35%', sm: '30%' } }}><strong>{translate('resources.rfid-transactions.show.createdAt')}</strong></TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{format(new Date(record.payment_details.created_at), 'PPp')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

// Main Transaction Details Component
const TransactionDetails = () => {
  return (
    <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
      <PageHeader />
      <TransactionInfo />
      <RfidUserInfo />
      <ChargingSessionDetails />
      <CostBreakdown />
      <PaymentDetailsCard />
      <BillingInfo />
      <Notes />
    </Box>
  );
};

export const RfidTransactionsShow = () => (
  <Show
    title={false}
    actions={false}
    component="div"
    sx={{
      backgroundColor: 'transparent',
      '& .RaShow-main': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    }}
  >
    <SimpleShowLayout
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
        '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
          px: { xs: 0, sm: 2 },
          py: { xs: 1, sm: 2 },
        },
        padding: { xs: 0, sm: 'inherit' },
      }}
    >
      <TransactionDetails />
    </SimpleShowLayout>
  </Show>
);

export default RfidTransactionsShow;
